import { Show, useObservable } from "@legendapp/state/react"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { cn, sharedStyles, trpc } from "@src/utils"
import { ActivityIndicator, Modal, Platform, Pressable, View } from "react-native"
import { Text, TextInput } from "./core"
import { SafeAreaView } from "react-native-safe-area-context"
import { useState } from "react"
import { useNumCols } from "@src/hooks"
import { List } from "./list"
import { ar } from "@src/i18n/ar"
import { Header } from "./Header"

export type InventoryCollectionPickerProps = {
  value?: number[]
  onChange: (ids: number[]) => void
  className?: string
}

export function InventoryCollectionPicker({ value = [], onChange, className }: InventoryCollectionPickerProps) {
  const collections = trpc.inventoryCollections.list.useInfiniteQuery({}, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  const selectedNames = collections.data?.pages
    .flatMap((p) => p.items)
    .filter((c) => value.includes(c.id))
    .map((c) => c.name)
    .join(", ")

  const displayText = selectedNames || ar.selectCollection
  const visible = useObservable(false)

  function hide() {
    visible.set(false)
  }

  function handleChange(ids: number[]) {
    onChange(ids)
    hide()
  }

  return (
    <>
      <Pressable
        className={cn("min-h-[45px] bg-white border border-gray-300 rounded px-4 justify-center active:opacity-70 flex-row items-center", className)}
        onPress={() => visible.set(true)}
      >
        <Text className={cn("flex-1", !selectedNames && "text-gray-400")} numberOfLines={1}>
          {displayText}
        </Text>
        <View className="absolute left-3">
          <MIcon name="chevron-down" size={24} color="#999" />
        </View>
      </Pressable>

      <Show if={visible}>
        <InventoryCollectionPickModal value={value} onChange={handleChange} hide={hide} />
      </Show>
    </>
  )
}

export function InventoryCollectionPickModal({ value: _value = [], onChange, hide }: InventoryCollectionPickerProps & { hide: () => void }) {
  const [searchText, setSearchText] = useState("")
  const collections = trpc.inventoryCollections.list.useInfiniteQuery({}, { getNextPageParam: (lastPage) => lastPage.nextCursor })
  const numCols = Math.max(2, useNumCols())
  const [value, setValue] = useState(_value)

  const flatItems = collections.data?.pages.flatMap((p) => p.items) || []
  const filteredCollections = flatItems.filter((c) => c.name.toLowerCase().includes(searchText.toLowerCase()))

  const handleItemPress = (id: number) => {
    setValue((v) => {
      if (v.includes(id)) return v.filter((v) => v !== id)
      return [...v, id]
    })
  }

  const header = (
    <Header
      title={ar.selectCollection}
      withoutInsets={Platform.OS === "ios"}
      right={
        <View className="flex-row h-full">
          <Pressable onPress={() => onChange(value)} className="h-full justify-center items-center px-4">
            <Text className="text-blue-500 font-bold">{ar.done}</Text>
          </Pressable>
          <Pressable onPress={hide} className="h-full justify-center items-center px-4">
            <MIcon name="close" size={24} color="#333" />
          </Pressable>
        </View>
      }
      canGoBack={false}
    />
  )

  return (
    <Modal animationType="slide" presentationStyle="formSheet" onRequestClose={hide} className="bg-gray-50" allowSwipeDismissal>
      {header}

      <View className="w-full max-w-content mx-auto px-2 py-2">
        <TextInput autoFocus value={searchText} onChangeText={setSearchText} placeholder={ar.search} />
      </View>

      <List
        data={filteredCollections}
        extraData={value}
        numColumns={numCols}
        contentContainerStyle={sharedStyles.contentContainer}
        key={`collection-${flatItems.length}`}
        keyExtractor={(item) => item.id.toString()}
        onEndReachedThreshold={0.45}
        onEndReached={() => {
          if (collections.hasNextPage && !collections.isFetchingNextPage) {
            collections.fetchNextPage()
          }
        }}
        ListEmptyComponent={collections.isLoading ? <ActivityIndicator size="small" className="py-2" /> : <Text className="text-center py-8 text-gray-500">{ar.noSections}</Text>}
        renderItem={({ item }) => {
          return (
            <Pressable
              className={cn("p-2 m-1 rounded border-b border-gray-100 bg-gray-50 justify-center relative", value.includes(item.id) && "bg-blue-100")}
              onPress={() => handleItemPress(item.id)}
            >
              <Text className="text-sm font-medium text-gray-800">{item.name}</Text>
              {value.includes(item.id) && (
                <View className="absolute top-2 inset-e-2 z-10 bg-blue-500 rounded-full pb-0.5">
                  <MIcon name="check" size={16} color="white" />
                </View>
              )}
            </Pressable>
          )
        }}
        ListFooterComponent={
          <>
            {collections.isFetchingNextPage && <ActivityIndicator className="py-4" />}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />
    </Modal>
  )
}
